'use client';

import { useEffect, useRef, useState } from 'react';

import { ModalContext } from '@/app/components/overlays/modal/context';
import { ModalStyled } from '@/app/components/overlays/modal/styles';
import { Overlay } from '@/app/components/overlays/overlay';
import type { ModalProps } from '@/app/components/overlays/utils/types';
import { useMountTransition } from '@/app/hooks/useMountTransition';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';

import styles from './index.module.css';

export const Modal = ({
  overlayKey,
  instanceId,
  isOpen,
  onClose,
  usePortal = true,
  overlayClassName,
  hasBackdrop = true,
  backdropBlur = 'xl',
  backdropClassName,
  autoScrollLock = true,
  canEscapeKeyClose = true,
  canOutsideClickClose = true,
  autoFocus = true,
  enforceFocus = true,
  children,
  contentProps,
  className,
  theme = 'light',
  sizes = 'sm',
  shadow = false,
  hasBorder = false,
  autoHeight = true,
  animateTransitionClassName = 'transition-opacity duration-500 ease-curve',
  animateBaseClassName,
  animateEnterClassName,
  animateExitClassName,
  ignoreClickTargetRef,
  isScrollOutside = false,
  scrollOutsideContainerRef,
  scrollOutsideWrapperClassName,
  scrollOutsideContainerClassName,
  ...props
}: ModalProps) => {
  const scrollOutsideContainerDefaultRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);

  const [isMounted, setIsMounted] = useState<boolean>(isOpen);

  const { className: animateMountClass, transitionState } = useMountTransition({
    isMount: isOpen,
    ref: contentRef,
    baseClassName: animateBaseClassName || cn('opacity-0'),
    animateClassName: animateEnterClassName || cn('opacity-100'),
    exitClassName: animateExitClassName || cn('opacity-0'),
  });

  useEffect(() => {
    setIsMounted(isOpen);
  }, [isOpen]);

  const handleTransitionEnd = (e: React.TransitionEvent<HTMLDivElement>) => {
    if (!isOpen && e.target === contentRef.current) {
      setIsMounted(false);
    }
  };

  if (!isMounted && !isOpen) return null;

  return (
    <Overlay
      overlayKey={overlayKey}
      instanceId={instanceId}
      isOpen={isOpen}
      onClose={onClose}
      usePortal={usePortal}
      overlayClassName={overlayClassName}
      hasBackdrop={hasBackdrop}
      backdropBlur={backdropBlur}
      backdropClassName={backdropClassName}
      transitionState={transitionState}
      autoScrollLock={autoScrollLock}
      canEscapeKeyClose={canEscapeKeyClose}
      canOutsideClickClose={canOutsideClickClose}
      autoFocus={autoFocus}
      enforceFocus={enforceFocus}
      clickOutsideContainerRef={
        isScrollOutside
          ? scrollOutsideContainerRef || scrollOutsideContainerDefaultRef
          : undefined
      }
      {...props}
    >
      <ModalContext.Provider
        value={{
          theme,
          shadow,
          hasBorder,
        }}
      >
        <div
          ref={contentRef}
          {...contentProps}
          tabIndex={-1}
          role="dialog"
          aria-modal="true"
          onTransitionEnd={handleTransitionEnd}
          className={cn(
            ModalStyled({ theme, sizes, shadow }),
            {
              fixed: usePortal,
              absolute: !usePortal,
              [styles.autoMaxHeight]: !isScrollOutside && autoHeight,
              'flex h-fit flex-col items-center md:h-fit md:max-w-none md:bg-transparent xl:max-w-none':
                isScrollOutside,
            },
            animateTransitionClassName,
            animateMountClass,
            className
          )}
          {...createDataAttributes({
            test: 'modal',
          })}
        >
          {isScrollOutside ? (
            <div
              className={cn(
                'flex size-full max-w-256 flex-col md:h-auto',
                scrollOutsideWrapperClassName
              )}
            >
              <div
                ref={
                  scrollOutsideContainerRef
                    ? undefined
                    : scrollOutsideContainerDefaultRef
                }
                className={cn('h-full', scrollOutsideContainerClassName)}
              >
                {children}
              </div>
            </div>
          ) : (
            children
          )}
        </div>
      </ModalContext.Provider>
    </Overlay>
  );
};
